/**
 * Owner of the "what have we already done for this (case, HP) pair?" question.
 *
 * Six module-level `__` guards in `useHangingProtocol.ts` used to answer it between them, read and
 * written inline in the middle of an effect. They were six independent bits with no coherent
 * transition table, each invalidated by a different subset of callers, which is the definition of a
 * state machine written as loose variables (VX-4810 slice 5, revisiting VX-4511).
 *
 * The bits are the same bits. What changes is that they are **not exported**, and that every read
 * and every write happens inside one of the `decide*` functions below. A caller receives a decision
 * and acts on it; it cannot claim half a guard, and it cannot reorder a claim against a bail.
 *
 * **Why that ordering is the whole point.** VX-4838 was not a bug in either of the two guards it
 * involved -- it was the order between them. Its bail ran *after* `__lastInitializedHPKey = hpKey`,
 * so the pass that later arrived with fresh data was short-circuited by the guard the same function
 * had just armed, and the steps were never published. Nothing in the types made the ordering
 * visible. Here the ordering is one function body, exercised by a transition-table test.
 *
 * **Why module scope rather than Context.** VX-4511 proposed hoisting this state into a React
 * Context provider and was closed Won't Do for a load-bearing reason: the components that call
 * `useHangingProtocol` unmount and remount during step navigation, and a Context that remounts
 * resets, which would let the init pass re-run and overwrite the user's unsaved edits with the
 * original GQL data. A provider that remounts is strictly worse than a module singleton. So the
 * state stays module-scoped and gains an explicit machine on top, which is what slice 5 asked for.
 *
 * Four instances of `useHangingProtocol` are mounted in a viewer (widget, layouts, viewport
 * overlays), and they share this state deliberately -- the guards coordinate *across* instances.
 */
import { makeHPKey } from './state';

// ─── Owned state: the six guards, none of them exported ─────────────────────────────────────────

// The last (case + HP smid) combination whose steps were published. Keyed by BOTH case and HP smid
// because the same HP smid can match different cases: keying on the smid alone let a navigation to a
// new case matching the same HP skip re-init and keep stale step state (VX-4429).
let lastPublishedHPKey: string | null = null;

// The last case+smid key written to `activeHangingProtocolSmidState`, so a transient null match (e.g.
// right after a clinical step delete triggers a refetch) doesn't cancel in-flight operations gated on
// hpSmid. Resettable from outside any single hook instance (VX-4615 review).
let lastActiveSmidKey: string | null = null;

// The hpKey whose re-init was deferred because an invalidation refetch was in flight. The pass that
// eventually applies the fresh data has to preserve unsaved step edits: the user can edit during the
// deferral window, and publishing writes both the steps atom and `clinicalStepsBaseline`, so applying
// without preservation discards the edit AND re-baselines it as clean -- the step then reads clean
// with the server's content (stepped-hp.e2e Phase 4c, cross-window:98, reorder-preserves-unsaved).
//
// This is the "capture the intent at enqueue time" rule: `preserveUnsavedEditsForHPKey` is set by the
// mutation paths and says nothing about a deferral, so the deferral records its own.
let deferredReinitForHPKey: string | null = null;

// Set when the publish guard is invalidated by a mutation elsewhere (this window or, via
// broadcastHPInvalidation, another viewer window), to the hpKey that was active at that moment. It
// tells the next initialization pass that IF it is re-reading that same HP, unsaved step edits must be
// carried across rather than replaced by the server's copy (VX-4739).
//
// Scoped to the hpKey rather than a bare boolean: `noteHPInvalidated()` is also called on HP delete,
// HP form submit (create or switch), and Reset All -- all legitimate transitions to a DIFFERENT
// hpKey. A bare flag would tell the next init to preserve "unsaved edits" from the HP the user just
// left, and `mergeUnsavedStepEdits`'s positional fallback (needed because a whole-HP update still
// doesn't return every server-assigned smid in every case -- see VX-4741) could then splice those
// stale edits onto an unrelated HP's steps whenever the two happened to have the same step count.
//
// A fresh page load never calls `noteHPInvalidated`, so this stays null. That no longer means a
// reload discards unsaved edits: the reload restores them from the persisted
// `unsavedClinicalStepEdits` record instead (VX-4803). This governs only the in-session route, where
// the live atoms are the source and positional splicing is allowed.
let preserveUnsavedEditsForHPKey: string | null = null;

// Smid of the hanging protocol the user just deleted, per window. Deleting an HP clears the step
// atoms, but it does not stop the queries already in flight for it, nor the independent background
// refetch usePrecacheQuery schedules, nor Apollo's previousData masking. Any of those can resolve
// afterwards still describing the deleted HP, and because the delete also invalidates the publish
// guard, that late response sails through and re-populates `steppedHPConfigurations` -- the step bar
// comes back and stays until a reload (VX-4738). It shows up most often on viewer/1, whose only
// knowledge of the deletion arrives over the invalidation broadcast.
//
// Held until the next deletion replaces it; backend smids are UUIDs and a deleted HP never returns,
// so there is nothing to clear it for, and a page load starts from null anyway.
let deletedHPSmid: string | null = null;

// Number of cross-window invalidation refetches currently in flight, across every mounted
// `useHangingProtocol` instance. While one is running, the window's `originalHangingProtocol` still
// holds the PREVIOUS protocol, and any re-render in that gap re-applies it. That is the write VX-4838
// is about. Measured with two windows, a case with priors and ~350ms latency:
//
//   43412ms v/0 wrote v8 with 4 steps after deleting two, and v/1 accepted it
//   46923ms v/1 received the invalidation and started refetching
//   47086ms v/1 re-initialised from its still-stale 6-step copy and wrote v9
//   47191ms v/0 took v9, so both windows showed 6 while the server held 4
//   51447ms v/1's refetch finally returned -- 4.4s after the damage
//
// `lastPublishedHPKey` does not cover this. It is cleared by every invalidation and only re-armed
// when a publish actually happens, so a refetch that comes back unchanged leaves it open
// indefinitely -- in the trace above it had been down since 34457ms, twelve seconds earlier.
// Ordering the guard invalidation after the refetch (VX-4770) was necessary but not sufficient for
// the same reason: it controls when the guard drops, not whether a render in the gap can write.
let invalidationRefetchesInFlight = 0;

// Notified on every change so the init effect can *re-run* when the count reaches zero. That is what
// makes the deferral below a deferral rather than a drop.
//
// It was a drop, and that regressed VX-4732/4733/4734/4739. The claim used to be "the effect runs
// again with the real data once the refetch lands", but nothing guaranteed it: an effect only re-runs
// when a dependency changes, and a refetch that resolves to an identical `originalHangingProtocol`
// reference changes none of them. The publish was then lost for good, the step bar kept whatever it
// had, and a step click could not become active. Surfacing the count through `useSyncExternalStore`
// makes it a real dependency, so dropping to zero always schedules the pass that applies fresh data.
const invalidationRefetchSubscribers = new Set<() => void>();

// Snapshotted before notifying: a subscriber can unsubscribe while being notified (a viewer window
// unmounting in response to the change is the ordinary case, and four instances of the hook are
// mounted per viewer), and mutating the Set mid-iteration would silently skip a subscriber that had
// not been visited yet.
const notifyInvalidationRefetchChange = (): void => {
  for (const notify of [...invalidationRefetchSubscribers]) notify();
};

// ─── Decisions ──────────────────────────────────────────────────────────────────────────────────

/**
 * What an init pass should do. Exhaustive on purpose: adding a case here is a compile error at the
 * call site until it is handled, which is the `StepLayoutContext` trick applied to the publish
 * decision rather than to a layout write.
 */
export type HPInitDecision =
  /** Stepped HP is off for this user. Never touch the step atoms; production behaviour unchanged. */
  | { kind: 'disabled' }
  /** A response for an HP that has been deleted. Stale by definition (VX-4738). */
  | { kind: 'ignore-deleted' }
  /** The persisted unsaved-edit record has not hydrated yet; publishing now would discard it. */
  | { kind: 'await-hydration' }
  /** Already published for this exact key. Only a rename can still need to land. */
  | { kind: 'sync-name-only' }
  /** `hpSmid` is transiently null mid-refetch; clearing and re-populating would flash empty chips. */
  | { kind: 'ignore-transient-null' }
  /** An invalidation refetch is outstanding, so the protocol in hand is pre-change (VX-4838). */
  | { kind: 'defer' }
  /** Publish the fetched steps. */
  | {
      kind: 'publish';
      /** Carry unsaved step edits across rather than replacing them with the server's copy. */
      preserveUnsavedEdits: boolean;
      /** This pass is the delayed half of an earlier deferral; the homing decision needs to know. */
      wasDeferred: boolean;
    };

/**
 * The single decision point for "is it safe to publish steps right now, and on what terms".
 *
 * Every guard read and every guard write for the init pass happens here, in this order. The order is
 * the product of VX-4838, VX-4770, VX-4783, VX-4738 and VX-4811 and is not safe to rearrange:
 *
 * 1. the feature gate clears the publish guard, so flipping the treatment on mid-session initialises
 * 2. a deleted HP is dropped **without** touching the publish guard, so the next real HP still runs
 * 3. hydration is awaited **before** the guard is claimed -- the guard is claimed once, so treating
 *    an unresolved IndexedDB read as "no unsaved edits" would discard them with no second chance
 * 4. an already-published key falls through to a name-only sync
 * 5. a transient null is ignored
 * 6. the deferral bails **above** the claim, leaving both the publish guard and the preserve flag
 *    untouched, so the later pass is not short-circuited by the guard this pass would have armed
 * 7. only then is the guard claimed and the preserve intent consumed
 */
/**
 * The case portion of an hpKey, which `makeHPKey` builds as `${caseId}:${hpSmid}`.
 *
 * Used to ask "did *this case* previously publish a protocol", which is the only sense in which a
 * null match can be called transient. Case smids are uuids and carry no colon, so the first one is
 * always the delimiter.
 */
const caseOf = (hpKey: string): string => hpKey.slice(0, hpKey.indexOf(':'));

export const decideStepInitPass = ({
  hpKey,
  hpSmid,
  isHPStepsEnabled,
  isUnsavedStepEditsHydrated,
  refetchesInFlight,
  hasFetchedSteps,
}: {
  hpKey: string;
  hpSmid: Nullable<string>;
  isHPStepsEnabled: boolean;
  isUnsavedStepEditsHydrated: boolean;
  /**
   * The in-flight count **as the calling render observed it**, via {@link subscribeToInvalidationRefetches}
   * -- not this module's live counter, which is why it is a parameter rather than a read.
   *
   * The two differ when a refetch starts between a render and its effect, and taking the live value
   * there would make the verdict depend on something outside the effect's dependency list. Passing
   * the rendered value keeps the decision a function of its inputs, which is also what makes the
   * transition table testable without faking timing.
   */
  refetchesInFlight: number;
  /** Whether the protocol in hand carries any steps. Decides whether preservation is even possible. */
  hasFetchedSteps: boolean;
}): HPInitDecision => {
  // Feature-flag gate: with Stepped HP off, never process step data so the viewer atoms stay at their
  // flat-HP defaults. Clear the publish guard so steps initialise cleanly if the flag flips on
  // mid-session (a Split cohort change without a reload); otherwise a stale key would make the later
  // flag-on pass skip initialisation.
  if (!isHPStepsEnabled) {
    lastPublishedHPKey = null;
    return { kind: 'disabled' };
  }

  // Leave the publish guard alone so the next response, for whichever HP actually matches now, still
  // initialises normally.
  if (hpSmid != null && hpSmid === deletedHPSmid) return { kind: 'ignore-deleted' };

  // The HP fetch this races is slower than a single-key IndexedDB read, so in practice nothing is
  // delayed by waiting here.
  if (!isUnsavedStepEditsHydrated) return { kind: 'await-hydration' };

  // A plain rename doesn't touch `.steps` at all, so it must not be blocked by the protection that
  // keeps a refetch from clobbering in-progress local step edits. Without the name-only route, a
  // window that didn't perform the rename itself (e.g. viewer/1 picking up a broadcast-triggered
  // refetch) would fetch the correct name and keep showing the stale one.
  if (hpSmid != null && hpKey === lastPublishedHPKey) return { kind: 'sync-name-only' };

  // Skip re-initialisation when the HP is temporarily null (e.g. during a refetch cycle). Without
  // this, a refetch that briefly returns null would clear the steps then re-populate them, flashing
  // empty step chips in dual-window mode.
  //
  // Scoped to the case, because "transient" is only meaningful relative to a protocol *this case*
  // had. `lastPublishedHPKey` is module-global and outlives navigation, so testing it alone meant
  // that once any case had published a protocol, every later case matching the Default HP -- where a
  // null smid is the settled answer, not a gap -- was misread as a refetch in progress. This branch
  // returns without stamping `steppedHPInitSettledFor`, so those cases never settled,
  // `activeStepBucketSelector` stayed null, and every viewport override read as absent: the grid hung
  // its images and then blanked. Comparing the case means a genuine mid-refetch null for the case in
  // hand still short-circuits, which is what this was for.
  if (
    hpSmid == null &&
    lastPublishedHPKey != null &&
    caseOf(lastPublishedHPKey) === caseOf(hpKey)
  ) {
    return { kind: 'ignore-transient-null' };
  }

  // Defer, before claiming anything. Two things make this a deferral and not a drop, and both are
  // load-bearing: the in-flight count is subscribed, so finishing the refetch re-runs the effect; and
  // returning here leaves `lastPublishedHPKey` and `preserveUnsavedEditsForHPKey` untouched, so that
  // later pass is not itself short-circuited by the already-published check above.
  if (refetchesInFlight > 0) {
    deferredReinitForHPKey = hpKey;
    return { kind: 'defer' };
  }

  lastPublishedHPKey = hpKey;

  const wasDeferred = hpKey === deferredReinitForHPKey;
  if (wasDeferred) deferredReinitForHPKey = null;

  // Never preserve across a protocol that no longer has steps. `mergeUnsavedStepEdits` can hand back
  // the previous list when the fetched one is null, which for a deleted HP means keeping a step bar
  // for a protocol that is gone -- VX-4859's symptom. There is no such thing as an unsaved edit to a
  // deleted protocol, so a null fetch always wins outright.
  //
  // Comparing hpKey (not just "was the flag set") is what keeps a switch to a different HP -- delete
  // falling back to Default, form submit creating/switching HPs, Reset All -- from adopting stale
  // edits left over from the HP the user just left.
  const preserveUnsavedEdits =
    hasFetchedSteps && (hpKey === preserveUnsavedEditsForHPKey || wasDeferred);
  preserveUnsavedEditsForHPKey = null;

  return { kind: 'publish', preserveUnsavedEdits, wasDeferred };
};

/** What a publish should do about the step index. */
export type HPStepIndexDecision =
  /** Nobody has positioned the index for this protocol: land on the first step. */
  | { kind: 'home' }
  /** Someone has already positioned it for this protocol; the only change owed is a clamp. */
  | { kind: 'clamp-only' };

/**
 * Whether a publish should home the step index to 0.
 *
 * **This asks about the index's owner, not about this window's history.** It used to consult a
 * `lastStepIndexHomedHPKey` guard, plus `wasDeferred` and `hasPendingStepCreates` as separate
 * suppressors on top -- three bits standing in for one question. Each was either too narrow or too
 * broad, and the spec's own record of this work has the pattern: every single boolean added here
 * traded one regression for another.
 *
 * The question that is actually being asked is **"is the reader already on a step of this
 * protocol?"**, and `ownerHPKey` on the index records exactly that:
 *
 * - a create in flight is owned, because `addStep` positioned the index on the step it created
 * - a deferred re-read of a protocol the *other* window positioned is owned, because the attribution
 *   crossed on the same broadcast message as the index (this is the case a per-window guard could not
 *   see: viewer/1 learns about a protocol created in viewer/0 purely by broadcast and never runs an
 *   init pass, so it had no local record and homed to 0 eight seconds late, dragging viewer/0 with it)
 * - a genuinely new protocol is unowned, so it homes -- **including one whose arrival happened to be
 *   deferred**, which the `wasDeferred` suppressor got wrong and which the spec recorded as a known
 *   gap: the reader could be left on step N of a protocol they had just navigated away from
 *
 * A null match is not a change of protocol. `hpSmid` drops to null and comes back during ordinary
 * refetches -- a save, a delete broadcast, a cross-window invalidation -- and `hpKey` carries that
 * through as `<case>:__no_hp__`. With no HP there are no steps and no step bar, so there is nothing to
 * home to; and because the index keeps naming the protocol it was positioned for, coming back to that
 * protocol is still correctly seen as owned.
 */
export const decideStepIndexHoming = ({
  hpKey,
  hpSmid,
  indexOwnerHPKey,
}: {
  hpKey: string;
  hpSmid: Nullable<string>;
  /** `ownerHPKey` from the attributed step index -- the protocol the index was positioned for. */
  indexOwnerHPKey: Nullable<string>;
}): HPStepIndexDecision => {
  if (hpSmid == null || hpKey === indexOwnerHPKey) return { kind: 'clamp-only' };
  return { kind: 'home' };
};

/**
 * Whether to write `smid` to `activeHangingProtocolSmidState` for this case.
 *
 * A `getBaseViewerData` refetch that transiently returns no matched HP (e.g. right after a clinical
 * step delete) would otherwise null the atom out immediately. Unlike the BroadcastChannel path, which
 * rejects stale null echoes in its mergeFn, this local writer has no equivalent guard, and a single
 * transient null cancels any in-flight operation gated on hpSmid -- e.g. the deferred step-deletion
 * timer. So a null for the *same case* is skipped as a refetch blip, while a genuine case switch to a
 * no-HP case still clears it, since the case segment of the key changes.
 *
 * Deleting the currently-active HP is the one case where a same-case null IS legitimate: the HP no
 * longer exists, so the refetch's null result must win. `noteActiveSmidGuardInvalidated()` is what
 * makes that write through (VX-4615 review).
 */
export const decideActiveSmidWrite = ({
  currentCaseId,
  smid,
}: {
  currentCaseId: Nullable<string>;
  smid: Nullable<string>;
}): boolean => {
  const caseKeyPrefix = `${currentCaseId ?? '__no_case__'}:`;
  if (smid == null && lastActiveSmidKey?.startsWith(caseKeyPrefix) === true) return false;
  lastActiveSmidKey = makeHPKey(currentCaseId, smid);
  return true;
};

// ─── Events ─────────────────────────────────────────────────────────────────────────────────────

/**
 * "The HP record changed, so re-read it." Drops the publish guard and, in the same step, records the
 * key it was holding so the next pass can tell a same-HP re-read from a switch to a different HP.
 *
 * Those two have to move together -- capturing the key after clearing it would capture null, and
 * clearing without capturing is what VX-4739 was.
 */
export const noteHPInvalidated = (): void => {
  preserveUnsavedEditsForHPKey = lastPublishedHPKey;
  lastPublishedHPKey = null;
};

/**
 * Reset the smid-write guard so the next render's smid -- including a legitimate transition to null,
 * e.g. deleting the currently-active HP with no other match -- is written instead of being mistaken
 * for a transient blip. Call anywhere HP data is invalidated, alongside {@link noteHPInvalidated}.
 */
export const noteActiveSmidGuardInvalidated = (): void => {
  lastActiveSmidKey = null;
};

/**
 * Record that a hanging protocol was deleted so this window ignores any later response that still
 * describes it. Call in the delete path, and on every other window via the invalidation broadcast.
 */
export const noteHangingProtocolDeleted = (smid: Nullable<string>): void => {
  deletedHPSmid = smid ?? null;
};

/**
 * Marked before the awaits and cleared in a `finally`: the point is to cover the renders that happen
 * while the refetch is outstanding, which is where the stale write lands.
 */
export const beginInvalidationRefetch = (): void => {
  invalidationRefetchesInFlight += 1;
  notifyInvalidationRefetchChange();
};

export const endInvalidationRefetch = (): void => {
  invalidationRefetchesInFlight = Math.max(0, invalidationRefetchesInFlight - 1);
  notifyInvalidationRefetchChange();
};

export const subscribeToInvalidationRefetches = (onChange: () => void): (() => void) => {
  invalidationRefetchSubscribers.add(onChange);
  return () => {
    invalidationRefetchSubscribers.delete(onChange);
  };
};

export const getInvalidationRefetchesInFlight = (): number => invalidationRefetchesInFlight;

/** Single reset seam for tests, replacing the five separate `__reset*` exports this module absorbed. */
export const resetHPInitMachineForTest = (): void => {
  lastPublishedHPKey = null;
  lastActiveSmidKey = null;
  deferredReinitForHPKey = null;
  preserveUnsavedEditsForHPKey = null;
  deletedHPSmid = null;
  invalidationRefetchesInFlight = 0;
  notifyInvalidationRefetchChange();
};
